import type { GET } from "../../apis/teams";
import type { TeamData } from "../../_model";
import { createEffect, createMemo, createSignal, For, onMount, Show, untrack } from "solid-js";
import { Button } from ":ui/form";

export type Props = {
  criteria: GET["returns"]["criteria"];
  team: TeamData;
  onSubmit: (props: { scores: Record<string, number>; next: boolean }) => Promise<void>;
  onGraded?: (props: { score: number; graded: boolean }) => void;
};
export default function Scoring(props: Props) {
  const criteria = props?.criteria;
  const criteria_keys = criteria == undefined ? [] : Object.keys(props.criteria);
  const submitted_max = criteria_keys.length;
  const MIN = 0;
  const MAX = 10;
  const [$scores, setScores] = createSignal({} as Record<string, number>);
  const $evaluated = createMemo(() => props.team.eval_id != undefined);
  const [$allow_submit, setAllowSubmit] = createSignal(false);
  const $all_submitted = createMemo(() => {
    const scores = $scores();
    let submitted = 0;
    for (const key in scores) {
      const value = scores?.[key];
      if (value == undefined) {
        break;
      }
      submitted++;
    }
    if (submitted >= submitted_max) {
      setAllowSubmit(true);
      return true;
    }
    setAllowSubmit(false);
    return false;
  });

  createEffect(() => {
    console.log("scores :: ", $scores());
  });

  createEffect(() => {
    const scores = props.team?.scores;
    setScores((s) => {
      for (const key of criteria_keys) {
        let value = scores?.[key];
        if (value != undefined) {
          const obj = criteria[key];
          const max = obj.max;
          const normal = value / max;
          const eq_value = normal * MAX;
          value = eq_value > MAX ? MAX : eq_value < MIN ? MIN : eq_value;
        }
        s[key] = value;
      }
      return { ...s };
    });
  });

  async function onSubmit(p: { next: boolean }) {
    const scores = $scores();
    // console.log("scores first :: ", scores);
    setAllowSubmit(false);
    const processed_scores = {};
    for (const key in criteria) {
      const obj = criteria[key];
      const max = obj.max;
      const value = scores[key];
      const normal = value / MAX;
      // const normal_for_max = value / max
      const eq_value = normal * max;
      // console.log("r is :: ", value, " :: ", normal, " :: ", max, " :: ", value / max, " :: ", eq_value);
      processed_scores[key] = eq_value;
    }
    await props.onSubmit?.({ scores: processed_scores, next: p.next });
    setAllowSubmit(true);
  }
  return (
    //
    <section class="flex flex-col w-full h-full border-border-card border-2 border-solid rounded-md px-2 py-2 overflow-y-auto overflow-x-hidden">
      <p class="text-20px font-bold py-2">Project Evaluation Criteria</p>
      <Show when={criteria_keys.length > 0} fallback={"no criteria provided!"}>
        <For each={criteria_keys}>
          {(key) => {
            const c = criteria[key];
            const name = c?.name;
            // let $value = createMemo(() => props.team.scores?.[key]);
            let ref: HTMLInputElement = undefined;
            createEffect(() => {
              const value = $scores()?.[key];
              // console.log("update ", key, " :: ", value);
              if (ref == undefined) {
                return;
              }
              const input_value = ref.value;
              if (String(value) !== input_value) {
                ref.value = String(value ?? "");
              }
            });

            function evaluate(props: { increase?: boolean; next?: number }) {
              const current = $scores()[key];
              // console.log("number is :: ", current);
              let next = undefined as number;
              if (props.next != undefined) {
                next = props.next;
              } else if (current == undefined) {
                next = 0;
              } else {
                next = props.increase ? current + 1 : current - 1;
                next = next > MAX ? MAX : next < MIN ? MIN : next; // limiting value between 0-10;
              }

              setScores((s) => {
                s[key] = next;
                return { ...s };
              });
            }
            return (
              //
              <div class="flex flex-row text-text-alt w-full min-h-fit h-full py-2 gap2 border-border-card border-t-1 border-solid overflow-hidden lt-md:(flex-wrap)">
                <p class="flex flex-row w-full h-fit text-16px">{name}</p>
                <div class="grid grid-cols-[35px_40px_35px] text-20px min-w-115px w-115px max-w-115px min-h-40px h-40px max-h-40px items-center content-center rounded-md border-border-card border-2 border-solid overflow-hidden children:(px-1 py-2 text-center items-center justify-center)">
                  <i
                    class="i-ic:baseline-plus w-full h-24px cursor-pointer"
                    onclick={() => evaluate({ increase: true })}
                  />
                  <input
                    ref={ref}
                    type="text"
                    class="border-border-card border-l-2 border-r-2 border-solid"
                    max={MAX}
                    min={MIN}
                    oninput={(e) => {
                      const value = e.target.value;
                      console.log("on input :: ", value);
                      // evaluate({ next: e.target.value })
                    }}
                  />
                  <i
                    class="i-lucide:minus w-full h-24px cursor-pointer"
                    onclick={() => evaluate({ increase: false })}
                  />
                </div>
              </div>
            );
          }}
        </For>
        <div class="flex flex-col items-center justify-between gap2 px-2">
          <Button
            controls={[]}
            onSubmit={() => onSubmit({ next: false })}
            class="form-btn !h-40px"
            triggers={{
              manualStatus: $allow_submit() ? "valid" : "invalid",
            }}
            statusInvalid="Finish Grading"
            statusValid={$evaluated() ? "Update" : "Submit"}
          />
          <Button
            controls={[]}
            onSubmit={() => onSubmit({ next: true })}
            class="form-btn !h-40px"
            triggers={{
              manualStatus: $allow_submit() ? "valid" : "invalid",
            }}
            events={{
              onSetButtonEnabledState(enable_button, status) {
                return $allow_submit() ? true : false;
              },
            }}
            statusInvalid="Finish Grading"
            statusValid={$evaluated() ? "Update And Next" : "Submit And Next"}
          />
        </div>
      </Show>
    </section>
  );
}
